// src/pages/admin/grn/AllGRN.jsx
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import AuthService from '../../../services/authService'
import { usePermissions } from '../../../hooks/usePermissions'
import ViewGRNModal from '../../../components/modals/ViewGRNModal'
import EditGRNModal from '../../../components/modals/EditGRNModal'
import PayGRNModal from '../../../components/modals/PayGRNModal'
import DeleteGRNModal from '../../../components/modals/DeleteGRNModal'
import InlineLoader from '../../../components/ui/InlineLoader'
import ButtonSpinner from '../../../components/ui/ButtonSpinner'
import { 
  Plus, 
  Search, 
  Filter, 
  Eye, 
  Edit, 
  Trash2,
  AlertCircle,
  Package,
  RefreshCw,
  Download,
  Calendar,
  Building,
  User,
  FileText,
  DollarSign,
  CheckCircle,
  XCircle,
  Clock,
  X
} from 'lucide-react'
import GRNService from '../../../services/grnService'
import BranchService from '../../../services/branchService'
import SuppliersService from '../../../services/suppliersService'
import { GRNModel } from '../../../utils/grnModel'

// Enhanced Toast Component
const Toast = ({ message, type, onClose }) => (
  <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center space-x-2 animate-in slide-in-from-right-full duration-300 ${
    type === 'success' 
      ? 'bg-green-500 text-white' 
      : type === 'error'
      ? 'bg-red-500 text-white'
      : type === 'warning'
      ? 'bg-yellow-500 text-white'
      : 'bg-blue-500 text-white'
  }`}>
    {type === 'success' ? <CheckCircle className="h-5 w-5" /> : 
     type === 'error' ? <XCircle className="h-5 w-5" /> : 
     <AlertCircle className="h-5 w-5" />}
    <span className="font-medium">{message || 'Action completed'}</span>
    <button onClick={onClose} className="ml-2 hover:bg-black hover:bg-opacity-20 rounded p-1">
      <X className="h-4 w-4" />
    </button>
  </div>
)

// Enhanced Stats Card Component
const StatsCard = ({ icon: Icon, title, value, color = "orange" }) => (
  <Card className="hover:shadow-md transition-shadow duration-200">
    <CardContent className="p-6">
      <div className="flex items-center">
        <div className={`p-3 rounded-lg bg-${color}-100`}>
          <Icon className={`h-6 w-6 text-${color}-600`} />
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </CardContent>
  </Card>
)

const AllGRN = () => {
  const navigate = useNavigate()

  // User and permissions
  const currentUser = AuthService.getCurrentUser()
  // eslint-disable-next-line no-unused-vars
  const permissions = usePermissions(currentUser)
  const userBranchId = currentUser?.branchId
  const isManager = currentUser?.role?.toLowerCase() === 'manager'
  
  console.log('🔐 AllGRN - User Access:', {
    user: currentUser?.name || currentUser?.username,
    role: currentUser?.role,
    branchId: userBranchId,
    isManager: isManager
  })

  // State management
  const [grns, setGRNs] = useState([])
  const [filteredGRNs, setFilteredGRNs] = useState([])
  const [loading, setLoading] = useState(true)
  // eslint-disable-next-line no-unused-vars
  const [actionLoading, setActionLoading] = useState({})
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [supplierFilter, setSupplierFilter] = useState('')
  const [branchFilter, setBranchFilter] = useState(isManager ? (userBranchId || '') : '')
  const [selectedGRN, setSelectedGRN] = useState(null)
  const [toast, setToast] = useState(null)
  const [stats, setStats] = useState({
    total: 0,
    paid: 0,
    unpaid: 0,
    totalAmount: 0
  })

  // Additional data for names resolution
  const [branches, setBranches] = useState([])
  const [suppliers, setSuppliers] = useState([])
  const [warehouses, setWarehouses] = useState([])
  const [dataLoading, setDataLoading] = useState(false)

  // Modal states
  const [showViewModal, setShowViewModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showPayModal, setShowPayModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 4000)
  }

  // Enhanced status configuration to ensure no question marks
  const getCleanPaymentStatusConfig = (paymentStatus) => {
    // Normalize status to lowercase and trim whitespace
    const normalizedStatus = paymentStatus?.toString().toLowerCase().trim()
    
    const statusMap = {
      'unpaid': {
        label: 'Unpaid',
        icon: '❌',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        borderColor: 'border-red-200'
      },
      'partially_paid': {
        label: 'Partially Paid',
        icon: '⏳',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        borderColor: 'border-yellow-200'
      },
      'partial': {
        label: 'Partially Paid',
        icon: '⏳',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        borderColor: 'border-yellow-200'
      },
      'fully_paid': {
        label: 'Fully Paid',
        icon: '✅',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-200'
      },
      'paid': {
        label: 'Fully Paid',
        icon: '✅',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-200'
      },
      'deleted': {
        label: 'DELETED',
        icon: '🗑️',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        borderColor: 'border-red-300'
      }
    }

    // Return the matching status or default
    const matchedStatus = statusMap[normalizedStatus]
    if (matchedStatus) {
      return matchedStatus
    }

    // If status contains "paid" anywhere, use appropriate style
    if (normalizedStatus?.includes('paid')) {
      if (normalizedStatus.includes('partial')) {
        return statusMap['partial']
      }
      return statusMap['paid']
    }

    // Default fallback
    return {
      label: paymentStatus || 'Unknown',
      icon: '❓',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-800',
      borderColor: 'border-gray-200'
    }
  }

  // Calculate stats
  const calculateStats = (grnList) => {
    const stats = {
      total: grnList.length,
      paid: 0,
      unpaid: 0,
      totalAmount: 0
    }

    grnList.forEach(grn => {
      const paymentStatus = calculatePaymentStatus(grn)
      if (paymentStatus === 'fully_paid' || paymentStatus === 'paid') {
        stats.paid++
      } else if (paymentStatus === 'unpaid') {
        stats.unpaid++
      }
      stats.totalAmount += parseFloat(grn.totalAmount) || 0
    })

    return stats
  }

  // Enhanced refresh function
  const handleRefresh = async () => {
    console.log('🔄 Manual refresh triggered')
    setDataLoading(true)
    
    try {
      await Promise.all([
        fetchBranches(),
        fetchSuppliers()
      ])
      await fetchGRNs()
      showToast('Data refreshed successfully', 'success')
    } catch (error) {
      console.error('❌ Error during refresh:', error)
      showToast('Error refreshing data', 'error')
    } finally {
      setDataLoading(false)
    }
  }

  // Fetch branches for name resolution
  const fetchBranches = async () => {
    try {
      const response = await BranchService.getAllBranches()
      if (response?.success) {
        const branchesData = response.data?.branches || []
        setBranches(branchesData)
        
        // Separate warehouses (branches with type 'warehouse')
        const warehousesData = branchesData.filter(branch => branch.type === 'warehouse')
        setWarehouses(warehousesData)
        
        console.log('✅ Fetched branches:', branchesData.length, 'warehouses:', warehousesData.length)
      }
    } catch (error) {
      console.error('❌ Error fetching branches:', error)
    }
  }

  // Fetch suppliers for name resolution
  const fetchSuppliers = async () => {
    try {
      const response = await SuppliersService.getAllSuppliers()
      if (response?.success) {
        const suppliersData = response.data?.suppliers || []
        setSuppliers(suppliersData)
        console.log('✅ Fetched suppliers:', suppliersData.length)
      }
    } catch (error) {
      console.error('❌ Error fetching suppliers:', error)
    }
  }

  // Get branch name by ID - ENHANCED VERSION
  const getBranchName = (branchIdOrObject) => {
    // If it's already an object with name, return the name  
    if (branchIdOrObject && typeof branchIdOrObject === 'object' && branchIdOrObject.name) {
      return branchIdOrObject.name
    }
    
    // If it's a string ID, find the branch in our branches array
    if (typeof branchIdOrObject === 'string') {
      const branch = branches.find(b => b._id === branchIdOrObject)
      return branch?.name || 'Unknown Branch'
    }
    
    return 'N/A'
  }

  // Get supplier/warehouse name by ID - FIXED VERSION
  const getSupplierName = (supplierOrWarehouse) => {
    if (!supplierOrWarehouse) return 'N/A'
    
    // If it's already an object with name, return the name
    if (typeof supplierOrWarehouse === 'object' && supplierOrWarehouse.name) {
      return supplierOrWarehouse.name
    }
    
    // If it's a string ID, try to find it in suppliers first, then warehouses
    if (typeof supplierOrWarehouse === 'string') {
      // First check suppliers
      const supplier = suppliers.find(s => s._id === supplierOrWarehouse)
      if (supplier) return supplier.name
      
      // Then check warehouses (which are branches with type 'warehouse')
      const warehouse = warehouses.find(w => w._id === supplierOrWarehouse)
      if (warehouse) return `${warehouse.name} (Warehouse)`
      
      // If not found in either, check all branches
      const branch = branches.find(b => b._id === supplierOrWarehouse)
      if (branch) return `${branch.name} (Branch)`
      
      // If still not found, return the ID with a label
      return `Unknown (${supplierOrWarehouse.slice(-8)})`
    }
    
    return 'N/A'
  }

  // ENHANCED: Calculate payment status with proper debugging
  const calculatePaymentStatus = (grn) => {
    console.log('🔍 Calculating payment status for GRN:', grn.grnNumber, {
      totalAmount: grn.totalAmount,
      paidAmount: grn.paidAmount,
      remainingPayment: grn.remainingPayment,
      paymentStatus: grn.paymentStatus,
      fullGRN: grn
    })

    // If GRN is soft deleted, return 'deleted'
    if (grn.isDeleted || grn.status === 'deleted') {
      return 'deleted'
    }

    // If GRN has explicit payment status, use it
    if (grn.paymentStatus) {
      console.log('✅ Using explicit paymentStatus:', grn.paymentStatus)
      return grn.paymentStatus
    }
    
    // Calculate based on amounts - Convert to numbers properly
    const totalAmount = parseFloat(grn.totalAmount) || 0
    const paidAmount = parseFloat(grn.paidAmount) || 0
    const remainingPayment = parseFloat(grn.remainingPayment)
    
    console.log('💰 Payment calculation values:', {
      totalAmount,
      paidAmount,
      remainingPayment,
      isRemainingPaymentValid: !isNaN(remainingPayment)
    })
    
    // If totalAmount is 0, consider it unpaid
    if (totalAmount <= 0) {
      console.log('⚠️ Total amount is 0 or negative, marking as unpaid')
      return 'unpaid'
    }
    
    // If we have valid remaining payment info, use it
    if (!isNaN(remainingPayment) && remainingPayment !== null && remainingPayment !== undefined) {
      console.log('✅ Using remainingPayment logic')
      if (remainingPayment <= 0) {
        console.log('✅ Remaining payment <= 0, status: fully_paid')
        return 'fully_paid'
      } else if (paidAmount > 0) {
        console.log('✅ Remaining payment > 0 but paid amount > 0, status: partially_paid')
        return 'partially_paid' 
      } else {
        console.log('✅ Remaining payment > 0 and no paid amount, status: unpaid')
        return 'unpaid'
      }
    }
    
    // Fallback calculation using percentage
    console.log('⚠️ Using fallback percentage calculation')
    const paidPercentage = (paidAmount / totalAmount) * 100
    console.log('📊 Paid percentage:', paidPercentage)
    
    if (paidPercentage >= 100) {
      console.log('✅ Paid percentage >= 100%, status: fully_paid')
      return 'fully_paid'
    } else if (paidPercentage > 0) {
      console.log('✅ Paid percentage > 0%, status: partially_paid')
      return 'partially_paid'
    } else {
      console.log('✅ Paid percentage = 0%, status: unpaid')
      return 'unpaid'
    }
  }

  // Fetch all GRNs - ENHANCED VERSION
  const fetchGRNs = async () => {
    try {
      setLoading(true)
      console.log('📦 Fetching all GRNs...')
      
      // For managers, add branch filter to API call
      let apiParams = {}
      if (isManager && userBranchId) {
        apiParams.branchId = userBranchId
        console.log('🏢 Manager branch filter applied to GRNs:', {
          branchId: userBranchId,
          branchName: 'Branch filtering enabled'
        })
      }
      
      const response = await GRNService.getAllGRNs(apiParams)
      console.log('📦 Raw API Response:', response)
      
      if (response?.success) {
        // Handle different possible response structures
        let grnData = []
        
        if (response.data?.grns) {
          grnData = response.data.grns
          console.log('📦 Using grns field from API response')
        } else if (response.data?.goodsReceiptNotes) {
          grnData = response.data.goodsReceiptNotes
          console.log('📦 Using goodsReceiptNotes field')
        } else if (response.data?.data) {
          grnData = response.data.data
          console.log('📦 Using nested data field')
        } else if (Array.isArray(response.data)) {
          grnData = response.data
          console.log('📦 Using direct data array')
        } else {
          console.warn('📦 Unexpected response structure:', response.data)
          grnData = []
        }
        
        // Additional frontend filtering for managers as security layer
        let filteredData = grnData
        if (isManager && userBranchId) {
          filteredData = grnData.filter(grn => {
            const branchId = grn.purchaseOrder?.requestingBranch?._id || grn.purchaseOrder?.requestingBranch
            return branchId === userBranchId
          })
          console.log('🔒 Manager frontend filter applied to GRNs:', {
            totalGRNs: grnData.length,
            filteredGRNs: filteredData.length,
            userBranchId: userBranchId
          })
        }
        
        console.log('✅ Processed GRN data:', filteredData)
        console.log('✅ Fetched', filteredData.length, 'GRNs')
        
        // Sample first GRN to understand structure
        if (filteredData.length > 0) {
          console.log('📦 Sample GRN structure:', filteredData[0])
          console.log('📦 Sample supplier/warehouse:', filteredData[0].purchaseOrder?.supplier || filteredData[0].purchaseOrder?.warehouse)
        }
        
        // Ensure filteredData is always an array
        const safeGRNData = Array.isArray(filteredData) ? filteredData : []
        setGRNs(safeGRNData)
        setFilteredGRNs(safeGRNData)
        setStats(calculateStats(safeGRNData))
      } else {
        console.error('❌ Failed to fetch GRNs:', response)
        showToast('Failed to fetch GRNs', 'error')
        setGRNs([])
        setFilteredGRNs([])
      }
    } catch (error) {
      console.error('❌ Error fetching GRNs:', error)
      showToast('Error fetching GRNs', 'error')
      setGRNs([])
      setFilteredGRNs([])
    } finally {
      setLoading(false)
    }
  }

  // Load all data
  const loadAllData = async () => {
    setDataLoading(true)
    await Promise.all([
      fetchBranches(),
      fetchSuppliers()
    ])
    await fetchGRNs() // Fetch GRNs after we have branch/supplier data
    setDataLoading(false)
  }

  // Filter GRNs based on search and filters
  const filterGRNs = () => {
    console.log('🔍 Filtering GRNs. Current grns state:', grns.length, 'items')
    console.log('🔍 Filter parameters:', {
      searchTerm,
      statusFilter,
      supplierFilter,
      branchFilter,
      isManager,
      userBranchId
    })
    
    if (!Array.isArray(grns)) {
      console.warn('GRNs is not an array:', grns)
      setFilteredGRNs([])
      return
    }

    let filtered = [...grns]

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(grn => {
        const grnNumber = grn.grnNumber?.toLowerCase() || ''
        const poNumber = grn.purchaseOrder?.poNumber?.toLowerCase() || ''
        const supplierName = getSupplierName(grn.purchaseOrder?.supplier || grn.purchaseOrder?.warehouse).toLowerCase()
        const branchName = getBranchName(grn.purchaseOrder?.requestingBranch).toLowerCase()
        const receivedBy = grn.receivedBy?.toLowerCase() || ''
        
        const searchLower = searchTerm.toLowerCase()
        
        return grnNumber.includes(searchLower) ||
               poNumber.includes(searchLower) ||
               supplierName.includes(searchLower) ||
               branchName.includes(searchLower) ||
               receivedBy.includes(searchLower)
      })
      console.log(`📝 Search filter applied: ${filtered.length} GRNs match "${searchTerm}"`)
    }

    // Status filter - use payment status instead of GRN status
    if (statusFilter) {
      filtered = filtered.filter(grn => {
        if (statusFilter === 'paid') {
          const paymentStatus = calculatePaymentStatus(grn)
          return paymentStatus === 'fully_paid'
        } else if (statusFilter === 'partial') {
          const paymentStatus = calculatePaymentStatus(grn)
          return paymentStatus === 'partially_paid'
        } else if (statusFilter === 'unpaid') {
          const paymentStatus = calculatePaymentStatus(grn)
          return paymentStatus === 'unpaid'
        } else {
          // For other status filters, use GRN status
          const derivedStatus = GRNModel.determineStatus(grn.receivedItems, grn)
          return derivedStatus === statusFilter
        }
      })
      console.log(`📊 Status filter applied: ${filtered.length} GRNs with status "${statusFilter}"`)
    }

    // Supplier filter - updated for dropdown selection
    if (supplierFilter) {
      filtered = filtered.filter(grn => {
        const supplierId = grn.purchaseOrder?.supplier?._id || grn.purchaseOrder?.supplier
        const warehouseId = grn.purchaseOrder?.warehouse?._id || grn.purchaseOrder?.warehouse
        return supplierId === supplierFilter || warehouseId === supplierFilter
      })
      console.log(`🏭 Supplier filter applied: ${filtered.length} GRNs for supplier "${supplierFilter}"`)
    }

    // Branch filter - updated for dropdown selection
    if (branchFilter) {
      filtered = filtered.filter(grn => {
        const branchId = grn.purchaseOrder?.requestingBranch?._id || grn.purchaseOrder?.requestingBranch
        return branchId === branchFilter
      })
      console.log(`🏢 Branch filter applied: ${filtered.length} GRNs for branch "${branchFilter}"`)
    }

    console.log('✅ Final filtered results:', filtered.length, 'GRNs')
    setFilteredGRNs(filtered)
  }

  // Handle GRN actions
  const handleViewGRN = async (grn) => {
    setSelectedGRN(grn)
    setShowViewModal(true)
  }

  const handlePayGRN = (grn) => {
    setSelectedGRN(grn)
    setShowPayModal(true)
  }

  const handleEditGRN = (grn) => {
    setSelectedGRN(grn)
    setShowEditModal(true)
  }

  const handleDeleteGRN = (grn) => {
    setSelectedGRN(grn)
    setShowDeleteModal(true)
  }

  // Navigate to Create GRN page
  const handleCreateGRN = () => {
    navigate('/admin/grn/create')
  }

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('')
    setStatusFilter('')
    setSupplierFilter('')
    setBranchFilter('')
  }

  // Export to CSV function
  const exportToCSV = () => {
    try {
      // Prepare CSV headers
      const headers = [
        'Date',
        'GRN Number',
        'PO Number',
        'Supplier/Warehouse',
        'Branch',
        'Total Amount (LKR)',
        'Payment Status',
        'Created By',
        'Created Date'
      ]

      // Prepare CSV data
      const csvData = filteredGRNs.map(grn => {
        const paymentStatus = calculatePaymentStatus(grn)
        return [
          formatDate(grn.receivedDate),
          grn.grnNumber,
          grn.purchaseOrder?.poNumber || 'N/A',
          getSupplierName(grn.purchaseOrder?.supplier || grn.purchaseOrder?.warehouse),
          getBranchName(grn.purchaseOrder?.requestingBranch),
          grn.totalAmount || 0,
          paymentStatus,
          grn.createdBy?.username || grn.createdBy?.name || 'Unknown',
          formatDate(grn.createdAt)
        ]
      })

      // Combine headers and data
      const csvContent = [headers, ...csvData]
        .map(row => row.map(field => `"${field}"`).join(','))
        .join('\n')

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      
      link.setAttribute('href', url)
      link.setAttribute('download', `grn-list-${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      showToast(`Successfully exported ${filteredGRNs.length} GRNs to CSV`, 'success')
    } catch (error) {
      console.error('❌ Error exporting to CSV:', error)
      showToast('Failed to export GRNs to CSV', 'error')
    }
  }

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  // Effects
  useEffect(() => {
    loadAllData()
  }, [])

  useEffect(() => {
    if (Array.isArray(grns)) {
      filterGRNs()
    }
  }, [searchTerm, statusFilter, supplierFilter, branchFilter, grns, branches, suppliers, warehouses])

  if (loading) {
    return (
      <InlineLoader text="Loading GRNs..." />
    )
  }

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}

      {/* Enhanced Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Goods Receipt Notes</h1>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            onClick={handleRefresh}
            variant="outline"
            className="flex items-center space-x-2 border-gray-300 hover:bg-gray-50"
            disabled={dataLoading}
          >
            <RefreshCw className={`h-4 w-4 ${dataLoading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          
          <Button
            onClick={exportToCSV}
            variant="outline"
            className="flex items-center space-x-2 border-gray-300 hover:bg-gray-50"
            disabled={filteredGRNs.length === 0}
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">Export</span>
          </Button>
          
          <Button
            onClick={handleCreateGRN}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create GRN
          </Button>
        </div>
      </div>

      {/* Enhanced Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          icon={Package}
          title="Total GRNs"
          value={stats.total.toLocaleString()}
          color="blue"
        />
        <StatsCard
          icon={CheckCircle}
          title="Fully Paid"
          value={stats.paid.toLocaleString()}
          color="green"
        />
        <StatsCard
          icon={XCircle}
          title="Unpaid"
          value={stats.unpaid.toLocaleString()}
          color="red"
        />
        <StatsCard
          icon={DollarSign}
          title="Total Amount"
          value={formatCurrency(stats.totalAmount)}
          color="purple"
        />
      </div>

      {/* Enhanced Filters Card */}
      <Card className="shadow-sm">
        <CardContent className="p-6">
          {/* Filter Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Filter GRNs</h2>
            </div>
            {(searchTerm || statusFilter || supplierFilter || branchFilter) && (
              <Button
                variant="outline"
                onClick={clearFilters}
                className="text-orange-500 border-orange-500 hover:bg-orange-50"
                size="sm"
              >
                Clear Filters
              </Button>
            )}
          </div>

          {/* Filter Controls */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <Input
                type="text"
                placeholder="Search by GRN No..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11 border-gray-300 focus:border-orange-500 focus:ring-orange-500"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.75rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em'
                }}
              >
                <option value="">All Status</option>
                <option value="incomplete">Incomplete</option>
                <option value="partial">Partial</option>
                <option value="complete">Complete</option>
                <option value="unpaid">Unpaid</option>
                <option value="partial">Partially Paid</option>
                <option value="paid">Fully Paid</option>
              </select>
            </div>

            {/* Supplier Filter */}
            <div className="relative">
              <select
                value={supplierFilter}
                onChange={(e) => setSupplierFilter(e.target.value)}
                className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.75rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em'
                }}
              >
                <option value="">All Suppliers</option>
                {suppliers.map(supplier => (
                  <option key={supplier._id} value={supplier._id}>
                    {supplier.name}
                  </option>
                ))}
                {warehouses.map(warehouse => (
                  <option key={warehouse._id} value={warehouse._id}>
                    {warehouse.name} (Warehouse)
                  </option>
                ))}
              </select>
            </div>

            {/* Branch Filter - disabled for managers who can only see their own branch */}
            <div className="relative">
              <select
                value={branchFilter}
                onChange={(e) => setBranchFilter(e.target.value)}
                disabled={isManager}
                className={`w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none appearance-none bg-white ${
                  isManager 
                    ? 'bg-gray-100 text-gray-500 cursor-not-allowed' 
                    : 'focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500'
                }`}
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.75rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em'
                }}
              >
                <option value="">
                  {isManager ? `${currentUser?.branch?.name || 'Your Branch'} (Current)` : 'All Branches'}
                </option>
                {!isManager && branches.filter(branch => branch.type !== 'warehouse').map(branch => (
                  <option key={branch._id} value={branch._id}>
                    {branch.name}
                  </option>
                ))}
              </select>
              {isManager && (
                <div className="absolute right-10 top-1/2 transform -translate-y-1/2">
                  <span className="text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded">
                    Branch Restricted
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Results Count */}
          <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-md">
            Showing <span className="font-medium text-gray-900">{filteredGRNs.length}</span> of <span className="font-medium text-gray-900">{grns.length}</span> GRNs
            {dataLoading && (
              <span className="ml-2 text-orange-600">
                <ButtonSpinner />
                Loading data...
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Enhanced GRN List */}
      <Card className="shadow-sm">
        <CardHeader className="border-b border-gray-200">
          <CardTitle className="flex items-center space-x-2">
            <Package className="h-5 w-5 text-orange-600" />
            <span>GRN Directory</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filteredGRNs.length > 0 ? (
            <div className="overflow-x-auto">
              {/* Desktop Table */}
              <div className="hidden lg:block">
                <table className="w-full min-w-full bg-white">
                  {/* Table Header */}
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        GRN No
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Supplier/Warehouse
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Branch
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Amount
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Payment Status
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created By
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  {/* Table Body */}
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredGRNs.map((grn) => {
                      const statusConfig = GRNModel.getStatusConfig(GRNModel.determineStatus(grn.receivedItems, grn))
                      
                      // Enhanced Payment status calculation with debugging
                      const paymentStatus = calculatePaymentStatus(grn)
                      const paymentConfig = getCleanPaymentStatusConfig(paymentStatus)
                      
                      console.log(`📋 GRN ${grn.grnNumber} - Payment Status: ${paymentStatus}, Config:`, paymentConfig)
                      
                      return (
                        <tr key={grn._id} className="hover:bg-gray-50 transition-colors duration-150">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <Calendar className="h-4 w-4 text-gray-400" />
                              <span className="text-gray-900">
                                {formatDate(grn.receivedDate)}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-3">
                              <div className="p-2 bg-orange-100 rounded-lg">
                                <Package className="h-4 w-4 text-orange-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{grn.grnNumber}</p>
                                <p className="text-sm text-gray-500">PO: {grn.purchaseOrder?.poNumber || 'N/A'}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <Building className="h-4 w-4 text-gray-400" />
                              <div className="max-w-xs">
                                <p className="font-medium text-gray-900 truncate" title={getSupplierName(grn.purchaseOrder?.supplier || grn.purchaseOrder?.warehouse)}>
                                  {getSupplierName(grn.purchaseOrder?.supplier || grn.purchaseOrder?.warehouse)}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-gray-900">
                              {getBranchName(grn.purchaseOrder?.requestingBranch)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              {/* <DollarSign className="h-4 w-4 text-gray-400" /> */}
                              <span className="text-gray-900 font-medium">
                                {formatCurrency(grn.totalAmount)}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span 
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${paymentConfig.bgColor} ${paymentConfig.textColor}`}
                              title={`Payment Status: ${paymentStatus} | Total: ${grn.totalAmount} | Paid: ${grn.paidAmount} | Remaining: ${grn.remainingPayment}`}
                            >
                              {/* <span className="mr-1">{paymentConfig.icon}</span> */}
                              {paymentConfig.label}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <User className="h-4 w-4 text-gray-400" />
                              <span className="text-gray-900">
                                {grn.createdBy?.username || grn.createdBy?.name || 'Unknown'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center space-x-1">
                              <button
                                onClick={() => handleViewGRN(grn)}
                                className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                                title="View GRN"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              {(() => {
                                // Check if GRN can be edited (payment is pending)
                                const remainingPayment = parseFloat(grn.remainingPayment)
                                const canEdit = !isNaN(remainingPayment) ? remainingPayment > 0 : 
                                  ['unpaid', 'partially_paid', 'partial'].includes(grn.paymentStatus?.toLowerCase())
                                
                                return canEdit ? (
                                  <button
                                    onClick={() => handleEditGRN(grn)}
                                    className="text-gray-400 hover:text-blue-600 p-1 rounded transition-colors"
                                    title="Edit GRN"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </button>
                                ) : null
                              })()}
                              <button
                                onClick={() => handlePayGRN(grn)}
                                className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                                title="Record Payment"
                              >
                                <DollarSign className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteGRN(grn)}
                                className="text-gray-400 hover:text-red-600 p-1 rounded transition-colors"
                                title="Delete GRN"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden space-y-4 p-4">
                {filteredGRNs.map((grn) => {
                  const paymentStatus = calculatePaymentStatus(grn)
                  const paymentConfig = getCleanPaymentStatusConfig(paymentStatus)
                  
                  return (
                    <Card key={grn._id} className="shadow-sm hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="space-y-3">
                          {/* GRN Header */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="p-2 bg-orange-100 rounded-lg">
                                <Package className="h-4 w-4 text-orange-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{grn.grnNumber}</p>
                                <p className="text-sm text-gray-500">PO: {grn.purchaseOrder?.poNumber || 'N/A'}</p>
                              </div>
                            </div>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${paymentConfig.bgColor} ${paymentConfig.textColor}`}>
                              <span className="mr-1">{paymentConfig.icon}</span>
                              {paymentConfig.label}
                            </span>
                          </div>

                          {/* GRN Details */}
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <p className="text-gray-500">Date</p>
                              <p className="text-gray-900">{formatDate(grn.receivedDate)}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Total Amount</p>
                              <p className="text-gray-900 font-medium">{formatCurrency(grn.totalAmount)}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Supplier/Warehouse</p>
                              <p className="text-gray-900 truncate">
                                {getSupplierName(grn.purchaseOrder?.supplier || grn.purchaseOrder?.warehouse)}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-500">Branch</p>
                              <p className="text-gray-900">{getBranchName(grn.purchaseOrder?.requestingBranch)}</p>
                            </div>
                          </div>

                          {/* Created By */}
                          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                            <div className="flex items-center space-x-2">
                              <User className="h-4 w-4 text-gray-400" />
                              <span className="text-sm text-gray-600">
                                Created by {grn.createdBy?.username || grn.createdBy?.name || 'Unknown'}
                              </span>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleViewGRN(grn)}
                                className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                                title="View GRN"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              {(() => {
                                // Check if GRN can be edited (payment is pending)
                                const remainingPayment = parseFloat(grn.remainingPayment)
                                const canEdit = !isNaN(remainingPayment) ? remainingPayment > 0 : 
                                  ['unpaid', 'partially_paid', 'partial'].includes(grn.paymentStatus?.toLowerCase())
                                
                                return canEdit ? (
                                  <button
                                    onClick={() => handleEditGRN(grn)}
                                    className="text-gray-400 hover:text-blue-600 p-1 rounded transition-colors"
                                    title="Edit GRN"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </button>
                                ) : null
                              })()}
                              <button
                                onClick={() => handlePayGRN(grn)}
                                className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                                title="Record Payment"
                              >
                                <DollarSign className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteGRN(grn)}
                                className="text-gray-400 hover:text-red-600 p-1 rounded transition-colors"
                                title="Delete GRN"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            </div>
          ) : (
            <div className="text-center py-12 px-6">
              <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {grns.length === 0 ? 'No GRNs found' : 'No GRNs match your filters'}
              </h3>
              <p className="text-gray-600 mb-4">
                {grns.length === 0 
                  ? 'Get started by creating your first GRN.' 
                  : 'Try adjusting your search criteria.'}
              </p>
              {grns.length === 0 && (
                <Button
                  onClick={handleCreateGRN}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create GRN
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      {showViewModal && selectedGRN && (
        <ViewGRNModal
          grn={selectedGRN}
          onClose={() => {
            setShowViewModal(false)
            setSelectedGRN(null)
          }}
        />
      )}

      {showEditModal && selectedGRN && (
        <EditGRNModal
          grn={selectedGRN}
          onClose={() => {
            setShowEditModal(false)
            setSelectedGRN(null)
          }}
          onSuccess={() => {
            setShowEditModal(false)
            setSelectedGRN(null)
            loadAllData()
            showToast('GRN updated successfully')
          }}
        />
      )}

      {showPayModal && selectedGRN && (
        <PayGRNModal
          grn={selectedGRN}
          onClose={() => {
            setShowPayModal(false)
            setSelectedGRN(null)
          }}
          onSuccess={() => {
            setShowPayModal(false)
            setSelectedGRN(null)
            loadAllData()
            showToast('Payment recorded successfully')
          }}
        />
      )}

      {showDeleteModal && selectedGRN && (
        <DeleteGRNModal
          grn={selectedGRN}
          onClose={() => {
            setShowDeleteModal(false)
            setSelectedGRN(null)
          }}
          onSuccess={() => {
            setShowDeleteModal(false)
            setSelectedGRN(null)
            loadAllData()
            showToast('GRN deleted successfully')
          }}
        />
      )}
    </div>
  )
}

export default AllGRN